import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { consumeMcpLoginContinuation } from '../utils/mcp-login-continuation'
import { homePath, orgPath } from '../utils/org-slug'

/**
 * Landing page for a successful OAuth callback redirect
 * (`/:orgSlug/oauth/complete?accessToken=...&refreshToken=...`). Adopts the
 * tokens the backend already issued and hands off to the normal app.
 */
export default function OAuthComplete() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithTokens } = useAuth()
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    const accessToken = searchParams.get('accessToken')
    const refreshToken = searchParams.get('refreshToken')

    if (!accessToken || !refreshToken) {
      toast.error('Sign-in failed — missing tokens')
      navigate(orgPath('/login'), { replace: true })
      return
    }

    void (async () => {
      try {
        await loginWithTokens(accessToken, refreshToken)
        toast.success(
          searchParams.get('passwordSetupSuggested') === '1'
            ? 'Signed in successfully. Set a password anytime from your profile.'
            : 'Signed in'
        )
        navigate(consumeMcpLoginContinuation() ?? homePath(), { replace: true })
      } catch {
        toast.error('Sign-in failed')
        navigate(orgPath('/login'), { replace: true })
      }
    })()
  }, [searchParams, loginWithTokens, navigate])

  return (
    <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      <p className="text-harbor-text-secondary">Signing you in…</p>
    </div>
  )
}
